import { isEmpty } from "lodash";

import { maxFileSize } from "../../constants/Config";
import {
  alphabetRegex,
  anyCharacters,
  emailRegex,
  nameWithSpecialCharRegex,
  numAlphabetOnlyRegex,
  numberOnlyRegex,
  passwordValidationRegex,
  personNameRegex,
  phoneFaxFormat,
  regexMBI,
  timeRegex,
  physicianNameValidationRegex,
  alphabetCityRegex,
  eligibilityNameRegex,
  cityPeriodRegex,
  allowWithoutAdditionalSpaceFreetextRegex,
} from "../../constants/Regex";

const streetName2 = {
  maxLength: { value: 100, message: "100 characters or digits are allowed." },
};
const DIGIT_MATCH = /^\d+$/;
const RESIDUES = [0, 2, 4, 6, 8, 1, 3, 5, 7, 9];
const luhn = (value: string) => {
  let result = false;
  if (value && DIGIT_MATCH.test(value)) {
    const  sum = value
      .split("")
      .reverse()
      .map((c) => parseInt(c))
      .map((c, i) => (i % 2 === 0 ? c : RESIDUES[c]))
      .reduce((sum, c) => sum + c, 0);
    result = sum % 10 === 0;
  }
  return result;
};
export const rules = {
  /**new validation messages */
  agencyEmail: {
    required: { value: true, message: `Agency email is required` },
    pattern: {
      value: emailRegex,
      message: "Please enter a valid agency email address",
    },
  },
  agencyPhoneNumber: {
    required: { value: true, message: `Agency phone number is required` },
    pattern: {
      value: phoneFaxFormat,
      message: "Please enter valid agency phone number.",
    },
  },
  agencyFaxNumber: {
    required: { value: true, message: `Agency fax number is required` },
    pattern: {
      value: phoneFaxFormat,
      message: "Please enter valid agency fax number.",
    },
  },
  socDateTimeRequired: {
    required: { value: true, message: "SOC date is required." },
  },
  dateRequired: {
    required: { value: true, message: "Date is required." },
  },
  /**----------------------- */
  email: {
    required: "Email is required",
    pattern: {
      value: emailRegex,
      message: "Please enter a valid email address",
    },
  },
  firstName: {
    required: { value: true, message: `First name is required.` },
    pattern: {
      value: nameWithSpecialCharRegex,
      message: "Please enter valid first name.",
    },
  },
  lastName: {
    required: { value: true, message: `Last name is required.` },
    pattern: {
      value: nameWithSpecialCharRegex,
      message: "Please enter valid last name.",
    },
  },
  lastNameOnlyAlphabets: {
    required: { value: true, message: `Last name is required.` },
    pattern: {
      value: eligibilityNameRegex,
      message: "Please enter valid last name.",
    },
  },
  phoneNumber: {
    required: { value: true, message: `Phone number is required` },
    pattern: {
      value: phoneFaxFormat,
      message: "Please enter valid phone number.",
    },
  },
  mfaTypeRequired: {
    required: { value: true, message: "MFA preference is required" },
  },
  mfaCode: {
    required: "MFA Code is Required.",
    pattern: {
      value: numberOnlyRegex,
      message: "Enter valid MFA Code",
    },
    maxLength: { value: 6, message: "Max length should be 6 digit." },
    minLength: {
      value: 6,
      message: "Min length should be 6 digit.",
    },
  },
  roleType: {
    required: "Role is required.",
  },
  agencyRequired: {
    required: "Agency is required.",
  },
  healthPlan: {
    required: "Health plan is required.",
  },
  subscriberId: {
    required: { value: true, message: `SubscriberID is required.` },
    pattern: {
      value: numAlphabetOnlyRegex,
      message: "Please enter valid SubscriberID.",
    },
  },
  dateOfBirthRequired: {
    required: { value: true, message: "Date of Birth is invalid." },
  },
  sendApprovedAuthTo: {
    required: {
      value: true,
      message: "Auth Preference is required.",
    },
    validate: (value: number) =>
    value !== 0 || "Auth Preference is required.",
  },
  npiRequired: {
    required: {
      message : "NPI is required",
      value: true,
    },
    validate: (value: string) => {



      const message = "Please enter valid NPI.";
      if (value.length !== 10 || !luhn("80840".concat(value))) return message;
      return true;
    },
  },
  zipcode: {
    required: { value: true, message: "Postal Code is required." },
    pattern: {
      value: numberOnlyRegex,
      message: "Please enter valid postal code.",
    },
  },
  state: {
    required: { value: true, message: "State is required." },
    pattern: {
      value: alphabetRegex,
      message: "Please enter valid state.",
    },
  },
  street1: {
    required: { value: true, message: "Street1 is required." },
  },
  street: {
    required: { value: true, message: "Street is required." },
  },
  county: {
    required: { value: true, message: "County is required." },
    pattern: {
      value: alphabetCityRegex,
      message: "Please enter valid county.",
    },
  },
  country: {
    required: { value: true, message: "Country is required." },
    pattern: {
      value: alphabetRegex,
      message: "Please enter valid country",
    },
  },
  city: {
    required: { value: true, message: "City is required." },
    pattern: {
      value: cityPeriodRegex,
      message: "Please enter valid city.",
    },
  },
  ntuReason: {
    required: { value: true, message: "NTU Reason is required." },
  },
  completedVisits: {
    required: { value: true, message: "Visit count required." },
  },
  discipline: {
    required: { value: true, message: `Skilled Discipline is required.` },
  },
  contactName: {
    required: { value: true, message: "Contact name is required." },
    pattern: {
      value: personNameRegex,
      message: "Please enter valid agency contact name.",
    },
  },
  title: {
    required: { value: true, message: "Title is required." },
    maxLength: { value: 128, message: "128 characters are allowed." },
  },
  description: {
    required: { value: true, message: "Description is required." },
    maxLength: { value: 256, message: "256 characters are allowed." },
  },
  bookmarkDescription: {
    maxLength: { value: 128, message: "128 characters are allowed." },
  },
  bookmarkTime: {
    required: { value: true, message: "Time is required." },
    pattern: {
      value: timeRegex,
      message: "Invalid time format.",
    },
  },
  videoFile: {
    required: { value: true, message: "Video file is required." },
    validate: {
      fileSize: (file: File | null) =>
        file && file.size <= maxFileSize
          ? true
          : "File size should not be greater than 75MB",
      fileType: (file: File | null) =>
        file && file.name.toLowerCase().endsWith(".mp4")
          ? true
          : "Only MP4 files are allowed.",
    },
  },
  password: {
    required: "Password field is required.",
    pattern: {
      value: passwordValidationRegex,
      message:
        "Password must be between 8 and 64 characters which includes uppercase, lowercase, a number, and a special character.",
    },
    maxLength: {
      value: 64,
      message: "Password length must not exceed 64 characters.",
    },
    minLength: {
      value: 8,
      message: "Password length must be at least 8 characters.",
    },
  },
  search: {
    required: "Search field should not be empty.",
  },
  eligibilityLastNameTextRegex: {
    required: { value: true, message: `Last name is required.` },
    pattern: {
      value: eligibilityNameRegex,
      message: "Please enter valid last name.",
    },
  },
  eligibilityFirstNameTextRegex: {
    required: { value: true, message: `First name is required.` },
    pattern: {
      value: eligibilityNameRegex,
      message: "Please enter valid first name.",
    },
  },
  mbiNumber: {
    pattern: {
      value: regexMBI,
      message: "Invalid MBI Number",
    },
    maxLength: { value: 11, message: "11 characters or digits are allowed." },
  },
  physicianName: {
    required: { value: true, message: "Physician name is required." },
  },
  facilityName: {
    required: { value: true, message: "Facility name is required." },
  },
  orderingPrescriber: {
    required: { value: true, message: "Ordering physician name is required." },
  },
  streetName1: {
    required: { value: true, message: "Street name 1 is required." },
  },
  streetName2,
  physicianZipcode: {
    required: { value: true, message: "Zip code is required." },
    maxLength: { value: 10, message: "Max length should be 10 digit." },
    pattern: {
      value: numberOnlyRegex,
      message: `Please enter valid zipcode.`,
    },
  },
  phone: {
    required: { value: true, message: `Phone no. is required.` },
    maxLength: { value: 14, message: "Max length should be 14 digit." },
    minLength: {
      value: 14,
      message: "Min length should be 10 digit.",
    },
    pattern: {
      value: phoneFaxFormat,
      message: "Please enter valid phone number.",
    },
  },
  phonePattern: {
    maxLength: { value: 14, message: "Max length should be 14 digit." },
    minLength: {
      value: 14,
      message: "Min length should be 10 digit.",
    },
    pattern: {
      value: phoneFaxFormat,
      message: "Please enter valid phone number.",
    },
  },
  fax: {
    required: { value: true, message: `Fax no. is required.` },
    maxLength: { value: 14, message: "Max length should be 14 digit." },
    minLength: { value: 14, message: "Min length should be 10 digit." },
    pattern: {
      value: phoneFaxFormat,
      message: "Please enter valid fax number.",
    },
  },
  agencyContactName: {
    required: { value: true, message: `Agency Contact Name is required.` },
    pattern: {
      value: personNameRegex,
      message: "Please enter valid agency contact name.",
    },
  },
  icd10Code: {
    required: {
      value: true,
      message: "ICD code is required",
    },
    maxLength: { value: 75, message: "75 characters are allowed" },
  },
  icd10CodeType: {
    required: {
      value: true,
      message: "ICD code type is required",
    },
  },
  icdCodes: {
    required: { value: true, message: "Please add ICD codes" },
    minLength: 1,
  },
  checkEligibilityHealthPlan: {
    required: { value: true, message: "Health plan id required" },
    pattern: { value: anyCharacters, message: "Please select valid plan." },
  },
  document: {
    required: { value: true, message: "Document is required." },
  },
  genderRequired: {
    required: { value: true, message: "Gender is required." },
  },
  languageRequired: {
    required: { value: true, message: "Language is required." },
  },
  requestorRequired: {
    required: { value: true, message: "Requestor is required." },
  },
  isDischargedRequired: {
    validate: (value: boolean | null | undefined) => typeof value === "boolean" ||
      "Please select whether the member has been discharged.",
  },
  dischargedDateRequired: {
    required: { value: true, message: "Date of Discharge is required." },
  },
  expectedDischargeDateRequired: {
    required: { value: true, message: "Expected Date of Discharge is required." },
  },
  referralReceivedDateRequired: {
    required: { value: true, message: "Referral Date is required." },
  },
  emailPattern: {
    pattern: {
      value: emailRegex,
      message: "Please enter a valid email address",
    },
  },
  validCity: {
    pattern: {
      value: cityPeriodRegex,
      message: "Please enter valid city.",
    },
  },
  validCounty: {
    pattern: {
      value: alphabetCityRegex,
      message: "Please enter valid county.",
    },
  },
  validState: {
    pattern: {
      value: alphabetRegex,
      message: "Please enter valid state.",
    },
  },
  validNpi: {
    validate: (value: string) => {
      const message = "Please enter valid NPI.";
      if (isEmpty(value) || (value && value.length === 0)) return true;
      if (value && (value.length !== 10 || !luhn("80840".concat(value)))) return message;
      return true;
    },
  },
  validPhysicianName: {
    pattern: {
      value: physicianNameValidationRegex,
      message: "Please enter valid physician name.",
    },
  },
  dischargeDate: {
    required: { value: true, message: "Discharge Date is invalid." },
  },
  nomncIssuedDate: {
    required: { value: true, message: "NOMNC Issued Date is invalid." },
  },
  notificationTitle: {
    required: { value: true, message: `Add notification title` },
    pattern: {
      value: allowWithoutAdditionalSpaceFreetextRegex,
      message: "Please enter valid notification title.",
    },
  },
  notificationTypeRequired: {
    required: { value: true, message: "Notification type is required." },
  },
  publishedDateRequired: {
    required: { value: true, message: "Published valid date is required." },
  },
};
